import axios from "axios";
import { sendFile } from "./utils";
import { OrganizationId } from "@/api/organizations";
import { ProjectId, ProjectKey, ProjectPermission } from "@/api/projects";
import type { TicketHistoryItem, TicketId, TicketKey } from "@/api/tickets";
import type { TeamId } from "@/api/teams";
import { UserActivityKind } from "@/api/generated/enums";

export { UserActivityKind };

export type UserSearchRequest = {
    anyNameField: string | null;
    userName: string | null;
    displayName: string | null;
    email: string | null;

    organizationId: OrganizationId | null;
    projectId: ProjectId | null;

    /** Filter by disabled state. Null returns both enabled and disabled users. */
    isDisabled: boolean | null;

    order: UserSearchOrder | null;
    direction: UserSearchDirection | null;
};

export type UserSearchResults = {
    id: UserId;
    userName: string;
    email: string;
    displayName: string | null;
    avatarId: AvatarId | null;
    /** Only populated for administrator callers; null otherwise. */
    isAdmin?: boolean | null;
    isDisabled: boolean;
    /** Only populated for administrator callers; null otherwise. */
    twoFactorEnabled?: boolean | null;
};

export type SearchResult = {
    users: UserSearchResults[];
    total: number;
};

export enum UserSearchOrder {
    DisplayName = 0,
    UserName = 1,
    Email = 2,
}

export enum UserSearchDirection {
    Ascending = 0,
    Descending = 1,
}

export type UserId = string & { __brand: "UserId" };
export type User = {
    id: UserId;
    userName: string;
    email: string;
    displayName: string | null;
    avatarId: AvatarId | null;
    /** Disabled users cannot sign in and are hidden from pickers, but still render on historical data. */
    isDisabled: boolean;
};

/**
 * The subset of a user needed to render them. Satisfied by `User`, `UserSearchResults` and
 * `AuthUser` alike, so display components do not care which one they were handed.
 */
export type DisplayUser = Pick<User, "userName" | "displayName" | "avatarId">;

/**
 * Disabled users must never be offered as a choice when picking someone to assign, subscribe, or
 * mention. They deliberately remain in the users store so historical data (existing assignees,
 * posted @mentions) still resolves, so pickers have to filter explicitly.
 */
export function isActiveUser(user: Pick<User, "isDisabled">) {
    return !user.isDisabled;
}

export type UserEditModel = {
    displayName: string | null;
    userName: string | null;
};

export type UserAdminEditModel = {
    email: string;
    displayName: string | null;
    /** Omit to keep the username; a new one rewrites the user's @mentions. */
    userName?: string;
};

/** One thing a user did: reporting, editing, commenting on, or resolving a ticket. */
export type UserActivityEvent = {
    id: string;
    date: Date;
    /** Who did it. Redundant on a profile feed, needed by the dashboard feed. */
    actorId: UserId;
    kind: UserActivityKind;
    ticketId: TicketId;
    projectId: ProjectId;
    ticketKey: TicketKey;
    ticketTitle: string;
    items: TicketHistoryItem[];
    /** Excerpt of the comment body, truncated server-side. Only set on comment events. */
    comment: string | null;
};

export type UserActivity = {
    total: number;
    events: UserActivityEvent[];
};

export type UserActivityDay = {
    /** UTC calendar day as YYYY-MM-DD. The grid is built in UTC so a day never drifts. */
    day: string;
    count: number;
};

export type UserActivityCounts = {
    openAssigned: number;
    resolved: number;
    comments: number;
    recentWindowDays: number;
};

export type UserTeam = {
    id: TeamId;
    name: string;
    organizationId: OrganizationId;
    organizationName: string;
    permissions: ProjectPermission[];
    projects: { id: ProjectId; key: ProjectKey; name: string }[];
};

export type UserProfile = {
    counts: UserActivityCounts;
    calendar: UserActivityDay[];
    teams: UserTeam[];
};

export type UserActivityResponse = Omit<UserActivity, "events"> & {
    events: (Omit<UserActivityEvent, "date"> & { date: string })[];
};

export function toUserActivity(data: UserActivityResponse): UserActivity {
    return {
        total: data.total,
        events: data.events.map((e) => ({ ...e, date: new Date(e.date) })),
    };
}

type UserProfileResponse = Omit<UserProfile, "calendar"> & {
    calendar: { date: string; count: number }[];
};

export type AvatarId = string & { __brand: "AvatarId" };
export type Avatar = {
    id: AvatarId;
    uploaderId: UserId | null;
    url: string;
};

export type UsersApi = {
    search(
        request: UserSearchRequest,
        page: number | null,
        perPage: number | null
    ): Promise<SearchResult>;
    createUser(email: string, displayName: string | null, userName: string | null): Promise<User>;
    adminEditUser(id: UserId, model: UserAdminEditModel): Promise<User>;
    setUserDisabled(id: UserId, isDisabled: boolean): Promise<User>;
    resetTwoFactor(id: UserId): Promise<void>;
    setUserAdmin(id: UserId, isAdmin: boolean): Promise<User>;
    /** Starts the background merge job and returns the Asio session id to watch its progress. */
    mergeUser(sourceId: UserId, targetId: UserId): Promise<string>;
    editSelf(model: UserEditModel): Promise<void>;
    getUsers(): Promise<User[]>;
    getProfile(id: UserId): Promise<UserProfile>;
    /** `day` narrows the feed to one UTC calendar day (YYYY-MM-DD), matching the activity calendar. */
    getActivity(
        id: UserId,
        page: number,
        perPage: number,
        day?: string | null
    ): Promise<UserActivity>;
    getAvatars(): Promise<Avatar[]>;
    uploadUserAvatar(file: File): Promise<Avatar>;
    uploadGenericAvatar(file: File): Promise<Avatar>;
    deleteUserAvatar(): Promise<void>;
};

export const userApi: UsersApi = {
    async search(request, page, perPage) {
        const params = new URLSearchParams();
        if (page != null) params.set("page", page.toString());
        if (perPage != null) params.set("perPage", perPage.toString());

        const { data: result } = await axios.post<SearchResult>(
            `/api/v0/users/search?${params}`,
            request
        );

        return result;
    },
    async createUser(email, displayName, userName) {
        const { data: user } = await axios.post<User>("/api/v0/users", {
            email,
            displayName,
            userName,
        });
        return user;
    },
    async adminEditUser(id, model) {
        const { data: user } = await axios.patch<User>(`/api/v0/users/${id}`, model);
        return user;
    },
    async setUserDisabled(id, isDisabled) {
        const { data: user } = await axios.patch<User>(`/api/v0/users/${id}`, { isDisabled });
        return user;
    },
    async resetTwoFactor(id) {
        await axios.post(`/api/v0/users/${id}/2fa/reset`);
    },
    async setUserAdmin(id, isAdmin) {
        const { data: user } = await axios.patch<User>(`/api/v0/users/${id}`, { isAdmin });
        return user;
    },
    async mergeUser(sourceId, targetId) {
        const { data } = await axios.post<{ sessionId: string }>("/api/v0/users/merge", {
            sourceUserId: sourceId,
            targetUserId: targetId,
        });
        return data.sessionId;
    },
    async editSelf(model) {
        await axios.put("/api/v0/users", model);
    },
    async getUsers() {
        const { data: users } = await axios.get<User[]>("/api/v0/users");
        return users;
    },
    async getProfile(id) {
        const { data } = await axios.get<UserProfileResponse>(`/api/v0/users/${id}/profile`);

        return {
            ...data,
            calendar: data.calendar.map((d) => ({ day: d.date.slice(0, 10), count: d.count })),
        };
    },
    async getActivity(id, page, perPage, day) {
        const { data } = await axios.get<UserActivityResponse>(`/api/v0/users/${id}/activity`, {
            params: { page, perPage, ...(day ? { day } : {}) },
        });

        return toUserActivity(data);
    },
    async getAvatars() {
        const { data: avatars } = await axios.get<Avatar[]>("/api/v0/users/avatars");
        return avatars;
    },
    async uploadUserAvatar(file) {
        return await sendFile<Avatar>("/api/v0/users/avatar", file);
    },
    async uploadGenericAvatar(file) {
        return await sendFile<Avatar>("/api/v0/users/avatars", file);
    },
    async deleteUserAvatar() {
        await axios.delete(`/api/v0/users/avatar`);
    },
};

export type IdentityError = {
    code: string;
    description: string;
};
